import {useMemo} from 'react';
import {v4 as uuid} from 'uuid';

import {
  NavigationCasus,
  NavigationCockpit,
  NavigationContacts,
  NavigationEmails,
  NavigationFinance,
  NavigationSearch,
  NavigationSetting,
  NavigationTasks,
  NavigationTimeTracking,
} from '_/assets/images';
import {useIsFeatureVisible} from '_/hooks/useFeatureFlagCheck';
import i18n from '_/i18n';
import {
  GlobalPermissions,
  HiddenModuleInfo,
  ModuleInfo,
} from '_/utilities/types';

import {useLastModulePathCache} from './useLastModulePathCache';

const addIdToModules = (entries: Omit<ModuleInfo, 'id'>[]): ModuleInfo[] =>
  entries.map(
    (entry): ModuleInfo => ({
      ...entry,
      id: entry.module,
    })
  );

const useGetModules = (): ModuleInfo[] => {
  const {
    cockpitPath,
    casusPath,
    contactsPath,
    searchPath,
    communicationPath,
    tasksPath,
    timeTrackingPath,
    invoicingPath,
  } = useLastModulePathCache();

  return addIdToModules([
    {
      Icon: NavigationCockpit,
      module: 'cockpit',
      modulePath: cockpitPath,
      text: i18n.t('common:modules.cockpit'),
    },
    {
      Icon: NavigationCasus,
      module: 'casus',
      modulePath: casusPath,
      text: i18n.t('common:modules.casus'),
    },
    {
      Icon: NavigationContacts,
      module: 'contacts',
      modulePath: contactsPath,
      text: i18n.t('common:modules.contacts'),
    },
    {
      Icon: NavigationEmails,
      module: 'communication',
      modulePath: communicationPath,
      text: i18n.t('common:modules.message'),
    },
    {
      Icon: NavigationTasks,
      module: 'tasks',
      modulePath: tasksPath,
      text: i18n.t('task:titlePlural'),
      requiredFeatureFlag: 'globalTasks',
    },
    {
      Icon: NavigationTimeTracking,
      module: 'time-tracking',
      modulePath: timeTrackingPath,
      text: i18n.t('common:modules.timetracking'),
    },
    {
      Icon: NavigationFinance,
      module: 'invoicing',
      modulePath: invoicingPath,
      text: i18n.t('common:modules.invoicing'),
      requiresOneOfPermissions: [
        GlobalPermissions.Invoicing.ManageAccountingExport,
        GlobalPermissions.Invoicing.ManageClientMoney,
        GlobalPermissions.Invoicing.ManageInvoices,
        GlobalPermissions.Invoicing.ManagePayments,
        GlobalPermissions.Invoicing.ManageTransactions,
      ],
    },
    {
      Icon: NavigationSearch,
      module: 'search',
      modulePath: searchPath,
      text: i18n.t('common:modules.search'),
    },
  ]);
};

const useGetSecondaryModules = (): ModuleInfo[] => {
  const {settingsPath} = useLastModulePathCache();

  return addIdToModules([
    {
      Icon: NavigationSetting,
      module: 'settings',
      modulePath: settingsPath,
      text: i18n.t('common:modules.settings'),
      requiresOneOfPermissions: GlobalPermissions.Settings.Admin,
    },
  ]);
};

/** getHiddenModules returns modules that are not shown in the left navigation bar but still have a route */
const getHiddenModules = (): HiddenModuleInfo[] => [
  {id: uuid(), module: 'user'},
];

/** useTranslatedNavModules is a hook to return the modules used for navigation with the right translation */
export const useTranslatedNavModules = (): {
  MODULES: ModuleInfo[];
  SECONDARY_MODULES: ModuleInfo[];
  HIDDEN_MODULES: HiddenModuleInfo[];
} => {
  const modules = useGetModules();
  const secondaryModules = useGetSecondaryModules();
  const checkIsFeatureVisible = useIsFeatureVisible();

  return useMemo(
    () => ({
      MODULES: modules.filter((module) =>
        checkIsFeatureVisible(module.requiredFeatureFlag)
      ),
      SECONDARY_MODULES: secondaryModules,
      HIDDEN_MODULES: getHiddenModules(),
    }),
    // next line is disabled so we can regenerate the data if necessary (user language change)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, checkIsFeatureVisible]
  );
};
